import 'types/communicator-globals';
import authenticate from 'communicator/external/authenticate';
import checkout from 'communicator/external/checkout/checkout';
import completeIdentityValidation from 'communicator/external/complete-identity-validation/complete-identity-validation';
import getSrcProfile from 'communicator/external/get-src-profile/get-src-profile';
import identityLookup from 'communicator/external/identity-lookup/identity-lookup';
import init from 'communicator/external/init';
import initiateIdentityValidation from 'communicator/external/initiate-identity-validation/initiate-identity-validation';
import isRecognized from 'communicator/external/is-recognized';
import unbindAppInstance from 'communicator/external/unbind-app-instance';

import appLoaded from 'communicator/internal/app-loaded';
import bankAppComplete from 'communicator/internal/bank-app-complete';
import biometricsComplete from 'communicator/internal/biometrics-complete';
import deleteIntent from 'communicator/internal/delete-intent';
import fetchConfigs from 'communicator/internal/fetch-configs';
import getCardBins from 'communicator/internal/get-card-bins';
import {
  getBankAppConfigHandler,
  getCardAndIssuerConfigs,
  getCvvConfigHandler,
  getFidoConfigHandler,
  getIssuerOtpConfigHandler
} from 'communicator/internal/get-stepup-config';
import getVbaToken from 'communicator/internal/get-vba-token';
import issuerOtpFlowsComplete from 'communicator/internal/issuer-otp-flows-complete';
import launchSRCi from 'communicator/internal/launch-srci';
import paymentData from 'communicator/internal/payment-data-api';
import pushEvents from 'communicator/internal/push-events';
import queueEvents from 'communicator/internal/queue-events';
import reset from 'communicator/internal/reset';
import sessionExpired from 'communicator/internal/session-expired';
import sessionRenewal from 'communicator/internal/session-renewal';
import signOut from 'communicator/internal/sign-out';
import spawnAuthProxy from 'communicator/internal/spawn-auth-proxy';
import srciLoaded from 'communicator/internal/srci-loaded';
import threeDsComplete from 'communicator/internal/threeds-complete';
import tncAccepted from 'communicator/internal/tnc-accepted';
import updateConfig from 'communicator/internal/update-config';
import visaSrciCancel from 'communicator/internal/visa-srci-cancel';
import visaSrciSuccess from 'communicator/internal/visa-srci-success';

import {
  getFeatureToggles,
  getIsFeatureToggleEnabled,
  getIsVisaSRCi,
  getOrgId,
  getSessionId,
  getVppsOrigin
} from 'communicator/store/selectors';
import { loadThm } from 'communicator/utils/thm';
import { listenToVisaSdk, sendToVisaSdk } from 'communicator/utils/visa-sdk';

import 'communicator/store/watchers';
import cvvComplete from './internal/cvv-complete';
import { loadVpps } from './utils/vpps';

// SRC spec apis
listenToVisaSdk('visa-sdk:external:authenticate', authenticate);
listenToVisaSdk('visa-sdk:external:checkout', checkout);
listenToVisaSdk('visa-sdk:external:completeIdentityValidation', completeIdentityValidation);
listenToVisaSdk('visa-sdk:external:getSrcProfile', getSrcProfile);
listenToVisaSdk('visa-sdk:external:identityLookup', identityLookup);
listenToVisaSdk('visa-sdk:external:init', init);
listenToVisaSdk('visa-sdk:external:initiateIdentityValidation', initiateIdentityValidation);
listenToVisaSdk('visa-sdk:external:isRecognized', isRecognized);
listenToVisaSdk('visa-sdk:external:unbindAppInstance', unbindAppInstance);

// Visa-internal apis
listenToVisaSdk('visa-sdk:internal:appLoaded', appLoaded);
listenToVisaSdk('visa-sdk:internal:bankAppComplete', bankAppComplete);
listenToVisaSdk('visa-sdk:internal:biometricsComplete', biometricsComplete);
listenToVisaSdk('visa-sdk:internal:cvvComplete', cvvComplete);
listenToVisaSdk('visa-sdk:internal:deleteIntent', deleteIntent);
listenToVisaSdk('visa-sdk:internal:fetchConfigs', fetchConfigs);
listenToVisaSdk('visa-sdk:internal:getCardBins', getCardBins);
listenToVisaSdk('visa-sdk:internal:getFidoConfig', getFidoConfigHandler);
listenToVisaSdk('visa-sdk:internal:getCvvConfig', getCvvConfigHandler);
listenToVisaSdk('visa-sdk:internal:getAuthConfigs', getBankAppConfigHandler);
listenToVisaSdk('visa-sdk:internal:getIssuerOtpConfigs', getIssuerOtpConfigHandler);
listenToVisaSdk('visa-sdk:internal:getCartArtAndIssuerConfigs', getCardAndIssuerConfigs);
listenToVisaSdk('visa-sdk:internal:getVbaToken', getVbaToken);
listenToVisaSdk('visa-sdk:internal:issuerOtpFlowsComplete', issuerOtpFlowsComplete);
listenToVisaSdk('visa-sdk:internal:launchAuthProxy', spawnAuthProxy);
listenToVisaSdk('visa-sdk:internal:launchSRCi', launchSRCi);
listenToVisaSdk('visa-sdk:internal:paymentData', paymentData);
listenToVisaSdk('visa-sdk:internal:pushEvents', pushEvents);
listenToVisaSdk('visa-sdk:internal:queueEvents', queueEvents);
listenToVisaSdk('visa-sdk:internal:reset', reset);
listenToVisaSdk('visa-sdk:internal:sessionExpired', sessionExpired);
listenToVisaSdk('visa-sdk:internal:sessionRenewal', sessionRenewal);
listenToVisaSdk('visa-sdk:internal:signOut', signOut);
listenToVisaSdk('visa-sdk:internal:srciLoaded', srciLoaded);
listenToVisaSdk('visa-sdk:internal:threeDsComplete', threeDsComplete);
listenToVisaSdk('visa-sdk:internal:tncAccepted', tncAccepted);
listenToVisaSdk('visa-sdk:internal:updateConfig', updateConfig);
listenToVisaSdk('visa-sdk:internal:visaSrciCancel', visaSrciCancel);
listenToVisaSdk('visa-sdk:internal:visaSrciSuccess', visaSrciSuccess);

// For VSB sdk integrations, THM is initialized by the merchant SDK. For other
// integrations, THM is loaded immediately in the /communicator
loadThm({
  initialLoad: true,
  isVisaSrci: getIsVisaSRCi(),
  orgId: getOrgId(),
  sessionId: getSessionId()
});

if (getIsFeatureToggleEnabled('enableVpps')) {
  loadVpps({
    vppsOrigin: getVppsOrigin(),
    sessionId: getSessionId()
  });
}

// send visa-sdk/merchant layer active feature-toggles
sendToVisaSdk('visa-communicator:internal:ft', getFeatureToggles());
